import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme,
  Tooltip,
  Chip,
  // Dialog,
  // DialogContent,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Info as InfoIcon,
  Timer as TimerIcon,
  LocationOn as LocationIcon,
  LiveHelp as SupportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import profileService from '../services/profileService';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { Dialog, DialogContent } from '@mui/material';
import TrialBanner from './subscription/TrialBanner';
import { formatDateLabel } from '../utils/subscriptionDates';

interface TopBarProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
}

const CITY_TO_TIMEZONE: Record<string, string> = {
  'sousse': 'Africa/Tunis',
  'tunis': 'Africa/Tunis',
  'sfax': 'Africa/Tunis',
  'monastir': 'Africa/Tunis',
  'bizerte': 'Africa/Tunis',
  'kairouan': 'Africa/Tunis',
  'gabes': 'Africa/Tunis',
  'gafsa': 'Africa/Tunis',
  'nabeul': 'Africa/Tunis',
  'hammamet': 'Africa/Tunis',
  'mahdia': 'Africa/Tunis',
  'casablanca': 'Africa/Casablanca',
  'rabat': 'Africa/Casablanca',
  'marrakech': 'Africa/Casablanca',
  'fez': 'Africa/Casablanca',
  'tangier': 'Africa/Casablanca',
  'algiers': 'Africa/Algiers',
  'oran': 'Africa/Algiers',
  'constantine': 'Africa/Algiers',
  'cairo': 'Africa/Cairo',
  'alexandria': 'Africa/Cairo',
  'giza': 'Africa/Cairo',
  'dubai': 'Asia/Dubai',
  'abu dhabi': 'Asia/Dubai',
  'sharjah': 'Asia/Dubai',
  'riyadh': 'Asia/Riyadh',
  'jeddah': 'Asia/Riyadh',
  'mecca': 'Asia/Riyadh',
  'medina': 'Asia/Riyadh',
  'paris': 'Europe/Paris',
  'lyon': 'Europe/Paris',
  'marseille': 'Europe/Paris',
  'toulouse': 'Europe/Paris',
  'london': 'Europe/London',
  'manchester': 'Europe/London',
  'birmingham': 'Europe/London',
  'berlin': 'Europe/Berlin',
  'munich': 'Europe/Berlin',
  'hamburg': 'Europe/Berlin',
  'frankfurt': 'Europe/Berlin',
  'new york': 'America/New_York',
  'los angeles': 'America/Los_Angeles',
  'chicago': 'America/Chicago',
  'houston': 'America/Chicago',
  'miami': 'America/New_York',
  'mumbai': 'Asia/Kolkata',
  'delhi': 'Asia/Kolkata',
  'bangalore': 'Asia/Kolkata',
  'hyderabad': 'Asia/Kolkata',
  'chennai': 'Asia/Kolkata',
  'aurangabad': 'Asia/Kolkata',
};

function isValidIANATimezone(tz: string): boolean {
  if (!tz || !tz.includes('/')) return false;
  if (tz.startsWith('Etc/') || tz.startsWith('GMT')) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function resolveTimezone(user: any): string {
  const city = user?.address?.city?.trim().toLowerCase();
  if (city && CITY_TO_TIMEZONE[city]) {
    return CITY_TO_TIMEZONE[city];
  }

  const explicitTz =
    typeof user?.timezone === 'string'
      ? user.timezone
      : (user?.timezone as any)?.name;

  if (explicitTz && isValidIANATimezone(explicitTz)) {
    return explicitTz;
  }

  return 'UTC';
}

const TopBar: React.FC<TopBarProps> = ({ onMenuToggle, sidebarOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, employee, userType, logout, updateUser } = useAuthStore();
  const { isTrialActive, trialDaysRemaining, trialTotalDays, subscription, currentPlan, limits, fetchSubscription } = useSubscriptionStore();
  const [nowText, setNowText] = useState('');
  const [accuracy, setAccuracy] = useState<'ok' | 'fallback'>('fallback');
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const subscriptionRefreshRef = useRef<number | null>(null);

  const tzName = resolveTimezone(user ?? employee?.agency);

  const roleDisplay =
    userType === 'employee' && employee
      ? `${(employee.role || '').replace('_', ' ').toUpperCase()} - ${employee.agency?.displayName || ''}`
      : null;

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat('EN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: tzName,
      }),
    [tzName]
  );

  const statusPollRef = useRef<number | null>(null);
  const statusSyncInFlightRef = useRef(false);
  const statusSyncBackoffUntilRef = useRef(0);
  const lastStatusRef = useRef<string | undefined>(user?.status);

  const forceLogoutWithMessage = (message: string) => {
    localStorage.setItem('agency_status_notice', message);
    logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (userType !== 'agency' || !user?._id) return;
    const blockedStatuses = ['suspended', 'rejected'];

    const syncStatus = async () => {
      if (document.visibilityState === 'hidden') return;
      if (statusSyncInFlightRef.current) return;
      if (Date.now() < statusSyncBackoffUntilRef.current) return;

      statusSyncInFlightRef.current = true;
      try {
        const fresh = await profileService.refreshStatus();
        statusSyncBackoffUntilRef.current = 0;
        const nextStatus = fresh?.status;
        const prevStatus = lastStatusRef.current;

        if (nextStatus && nextStatus !== prevStatus) {
          lastStatusRef.current = nextStatus;
          if (blockedStatuses.includes(nextStatus)) {
            forceLogoutWithMessage(
              nextStatus === 'suspended'
                ? 'Your agency has been suspended. Please contact support.'
                : 'Your agency application was rejected. Please contact support.'
            );
            return;
          }

          updateUser(fresh);
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        const isTimeout = message.toLowerCase().includes('timeout');
        statusSyncBackoffUntilRef.current = Date.now() + (isTimeout ? 120000 : 30000);

        if (!isTimeout) {
          console.warn('Failed to refresh agency status:', e);
        } else {
          console.debug('Agency status refresh timed out; backing off before retrying.');
        }
      } finally {
        statusSyncInFlightRef.current = false;
      }
    };

    syncStatus();
    statusPollRef.current = window.setInterval(syncStatus, 60000);
    window.addEventListener('focus', syncStatus);
    document.addEventListener('visibilitychange', syncStatus);

    return () => {
      if (statusPollRef.current) {
        window.clearInterval(statusPollRef.current);
      }
      window.removeEventListener('focus', syncStatus);
      document.removeEventListener('visibilitychange', syncStatus);
    };
  }, [logout, navigate, updateUser, user?._id, userType]);

  useEffect(() => {
    const updateTime = () => {
      try {
        const now = new Date();
        setNowText(formatter.format(now));
        setAccuracy('ok');
      } catch (err) {
        setNowText(new Date().toLocaleTimeString());
        setAccuracy('fallback');
      }
    };

    updateTime();
    intervalRef.current = window.setInterval(updateTime, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [formatter]);

  useEffect(() => {
    if (userType !== 'agency' || (user?.status !== 'approved' && user?.status !== 'under_review')) {
      return;
    }

    const refreshEveryMs = 5 * 60 * 1000;

    const refreshSubscriptionState = () => {
      if (document.visibilityState === 'hidden') return;
      fetchSubscription();
    };

    refreshSubscriptionState();
    subscriptionRefreshRef.current = window.setInterval(refreshSubscriptionState, refreshEveryMs);
    window.addEventListener('focus', refreshSubscriptionState);
    document.addEventListener('visibilitychange', refreshSubscriptionState);
    window.addEventListener('online', refreshSubscriptionState);

    return () => {
      if (subscriptionRefreshRef.current) {
        window.clearInterval(subscriptionRefreshRef.current);
        subscriptionRefreshRef.current = null;
      }
      window.removeEventListener('focus', refreshSubscriptionState);
      document.removeEventListener('visibilitychange', refreshSubscriptionState);
      window.removeEventListener('online', refreshSubscriptionState);
    };
  }, [fetchSubscription, user?.status, userType]);

  const showTrialChip = isTrialActive && trialDaysRemaining > 0;
  const trialChipDays = trialDaysRemaining;
  const trialEndDateText = subscription?.trial?.trialEndDate
    ? formatDateLabel(subscription.trial.trialEndDate)
    : null;
  const isDemoAccount = localStorage.getItem('is_demo_account') === 'true';

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        color: '#111827',
        borderBottom: 'none',
        zIndex: theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuToggle}
            sx={{
              display: { xs: 'block', md: sidebarOpen ? 'none' : 'block' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {getPageTitle(window.location.pathname, t)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <LanguageSwitcher />

          {/* Demo Account Indicator */}
          {isDemoAccount && (
            <Tooltip title={t('topBar.demoAccount')} arrow>
              <Chip
                label="DEMO"
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: '#FFFBEB',
                  color: '#D97706',
                  border: '1px solid #FDE68A',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}

          {/* Employee Role Display */}
          {userType === 'employee' && roleDisplay && (
            <Tooltip
              title={t('topBar.employeeRole', {
                role: (employee?.role || '').replace('_', ' '),
              })}
              arrow
            >
              <Chip
                label={roleDisplay}
                size="small"
                sx={{
                  fontWeight: 500,
                  bgcolor: '#F3F4F6',
                  color: '#4B5563',
                  border: '1px solid #E5E7EB',
                  flexShrink: 1,
                  maxWidth: { xs: 80, sm: 150, md: 250 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </Tooltip>
          )}

          {/* Trial Days Counter */}
          {showTrialChip && (
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="600" display="block">
                    {t('topBar.trialTitle', { count: trialChipDays })}
                  </Typography>
                  {trialEndDateText && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                      {t('topBar.trialEndsOn', { date: trialEndDateText })}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                icon={<TimerIcon sx={{ fontSize: '16px !important' }} />}
                label={t('topBar.trialChip', { count: trialChipDays })}
                size="small"
                onClick={() => setTrialDialogOpen(true)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  bgcolor: trialChipDays <= 3 ? '#FEF2F2' : '#F3F4F6',
                  color: trialChipDays <= 3 ? '#EF4444' : '#111827',
                  border: '1px solid',
                  borderColor: trialChipDays <= 3 ? '#FCA5A5' : '#E5E7EB',
                  flexShrink: 0,
                  '&:hover': { bgcolor: trialChipDays <= 3 ? '#FEE2E2' : '#E5E7EB' },
                }}
              />
            </Tooltip>
          )}

          {/* Trial Details Dialog */}
          <Dialog
            open={trialDialogOpen}
            onClose={() => setTrialDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            BackdropProps={{
              sx: {
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: 5,
                bgcolor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
                border: 'none'
              }
            }}
          >
            <DialogContent sx={{ p: 0, overflow: 'visible' }}>
              <TrialBanner
                trialDaysRemaining={trialDaysRemaining}
                trialTotalDays={trialTotalDays}
                planName={currentPlan?.displayName}
                planTier={currentPlan?.planId as any}
                planLimits={limits as any}
                variant="modal"
                onDismiss={() => setTrialDialogOpen(false)}
                onUpgrade={() => {
                  setTrialDialogOpen(false);
                  navigate('/pricing');
                }}
              />
            </DialogContent>
          </Dialog>

          {/* Current plan display */}
          {subscription && (
            <Tooltip title={t('topBar.currentPlan')} arrow>
              <Chip
                label={
                  subscription.status === 'trialing'
                    ? `Procès: ${subscription.trial?.trialPlanId?.replace('basic', 'Drive Basic').replace('pro', 'Drive Pro').replace('elite', 'Drive Elite').replace('growth', 'Drive Pro') || 'Drive Pro'}`
                    : ((typeof subscription.plan === 'object' ? subscription.plan.planId : subscription.plan) || 'basic')
                      .toString()
                      .replace('basic', 'Drive Basic')
                      .replace('pro', 'Drive Pro')
                      .replace('growth', 'Drive Pro')
                      .replace('elite', 'Drive Elite')
                }
                size="small"
                onClick={() => navigate('/subscription')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  bgcolor: '#F3F4F6',
                  color: '#4B5563',
                  border: '1px solid #E5E7EB',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#E5E7EB' },
                }}
              />
            </Tooltip>
          )}

          {/* Time & Timezone Pill */}
          <Box
            sx={{
              px: 1.5,
              py: 0.6,
              minHeight: 36,
              borderRadius: '50px',
              bgcolor: '#F3F4F6',
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
            >
              {nowText}
            </Typography>
            <Box sx={{ width: '1px', height: 14, bgcolor: '#D1D5DB', flexShrink: 0 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <LocationIcon sx={{ fontSize: 14, color: '#6B7280' }} />
                <Typography
                  variant="caption"
                  sx={{ color: '#111827', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {user?.address?.city ||
                    (tzName?.includes('/')
                      ? tzName.split('/').pop()?.replace('_', ' ')
                      : 'UTC')}
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: 10, bgcolor: '#D1D5DB', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{ color: '#6B7280', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                {tzName}
              </Typography>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: accuracy === 'ok' ? '#22C55E' : '#F59E0B',
                  flexShrink: 0,
                }}
              />
            </Box>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="600" display="block">
                    {t('topBar.systemTime')}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                    {t('topBar.systemTimeInfo', { timezone: tzName })}
                  </Typography>
                </Box>
              }
              arrow
            >
              <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {/* Support Button */}
          <Tooltip title={t('topBar.support') || 'Support Tickets'} arrow>
            <IconButton
              size="small"
              onClick={() => navigate('/support')}
              sx={{
                color: '#FF690F',
                bgcolor: 'rgba(255, 105, 15, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 105, 15, 0.2)' },
                flexShrink: 0,
              }}
            >
              <SupportIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <NotificationBell />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const getPageTitle = (pathname: string, t: (key: string) => string): string => {
  const routes: Record<string, string> = {
    '/dashboard': t('dashboard.title'),
    '/vehicles': t('vehicles.title'),
    '/bookings': t('bookings.title'),
    '/contracts': t('contracts.title'),
    '/invoices': t('invoices.title'),
    '/pos': t('pos.title'),
    '/pos/dashboard': t('pos.dashboard'),
    '/profile': t('profile.title'),
    '/onboarding': t('profile.settings'),
  };
  return routes[pathname] || t('dashboard.title');
};

export default TopBar;
